import React, { useState } from "react";
import { createPortal } from "react-dom";
import styles from "./Exit.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { setIsUser } from "../../../redux/Slices/userSlice";
import LoadingComponent from "../../LodingComponent/LoadingComponent";
import LoginService from "../../../pages/login/loginService/LoginService";
import { persistor, RootState } from "../../../redux/store";
import { clearBasket } from "../../../redux/Slices/BasketSlice";
import { eraseCookie } from "../../../api/utils/utils";

interface ExitComponentProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const ExitComponent: React.FC<ExitComponentProps> = ({
  isOpen = true,
  onClose,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const translations = useSelector(
    (state: RootState) => state.translations.data
  );

  if (!isOpen) {
    return null;
  }

  const handleCancel = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (onClose) {
      onClose();
      return;
    }
    window.history.back();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCancel(e);
    }
  };

  const handleLogout = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setLoading(true);

    const token = localStorage.getItem("token");

    // 1. Send logout request to backend WHILE token is still in localStorage
    // (So axios interceptor can attach Authorization: Bearer <token>)
    if (token) {
      try {
        await Promise.race([
          LoginService.logout().catch((err) => {
            console.error("Logout request error:", err);
          }),
          new Promise((resolve) => setTimeout(resolve, 1500)),
        ]);
      } catch (err) {
        console.error("Logout error:", err);
      }
    }

    // 2. Clear all local storage and session data
    localStorage.removeItem("token");
    localStorage.removeItem("selectedBranch");
    localStorage.removeItem("name");
    localStorage.removeItem("phone");
    localStorage.removeItem("deliveryOption");
    localStorage.removeItem("deliveryDetails");
    eraseCookie("x-guest-token");
    eraseCookie("guest-token");
    dispatch(setIsUser(false));
    dispatch(clearBasket());

    try {
      await persistor.purge();
    } catch (err) {
      console.error("Persistor purge error:", err);
    }

    setLoading(false);

    if (onClose) {
      onClose();
    }

    // 3. Clean full page redirect to login
    window.location.href = "/login";
  };

  const modalContent = (
    <div
      className={styles.modalOverlay}
      onClick={handleBackdropClick}
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="logout-dialog-title"
    >
      {loading ? <LoadingComponent /> : null}
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <p id="logout-dialog-title" className={styles.modalTitle}>
          {translations?.logout_ask || "Hesabdan çıxmaq istəyirsiz?"}
        </p>
        <div className={styles.buttons_container}>
          <button
            type="button"
            onClick={handleLogout}
            onMouseDown={(e) => e.stopPropagation()}
            className={styles.logout_btn}
            aria-label={translations?.logout || "Hesabdan çıx"}
            tabIndex={0}
          >
            {translations?.logout || "Hesabdan çıx"}
          </button>
          <button
            type="button"
            className={styles.cancel_btn}
            onClick={handleCancel}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label={translations?.cancel || "Ləğv et"}
            tabIndex={0}
          >
            {translations?.cancel || "Ləğv et"}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : modalContent;
};

export default ExitComponent;
